import React, { useCallback } from 'react';
import { VaButtonPair } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import {
  useNavigate,
  useParams,
  useLocation,
} from 'react-router-dom-v5-compat';
import Wrapper from '../layout/Wrapper';
import AppointmentCard from '../components/AppointmentCard';
import {
  useGetAppointmentQuery,
  useCancelAppointmentMutation,
} from '../redux/api/vassApi';
import { FLOW_TYPES, URLS } from '../utils/constants';
import { pathWithSearch } from '../utils/invitationQuery';
import {
  isCancellationFailedError,
  isMissingParameterError,
  isServerError,
  isAppointmentNotFoundError,
} from '../utils/errors';

const getLoadingMessage = isCanceling => {
  if (isCanceling) {
    return 'Canceling appointment. This may take up to 30 seconds. Please don’t refresh the page.';
  }
  return 'Loading appointment details. This may take up to 30 seconds. Please don’t refresh the page.';
};

const CancelAppointment = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    data: appointmentData,
    isLoading,
    error: appointmentError,
  } = useGetAppointmentQuery({
    appointmentId,
  });
  const [
    cancelAppointment,
    { isLoading: isCanceling, error: cancelAppointmentError },
  ] = useCancelAppointmentMutation();

  const onCancelAppointment = useCallback(async () => {
    const result = await cancelAppointment({ appointmentId });
    if (result.error) {
      return;
    }
    navigate(
      pathWithSearch(
        `${URLS.CANCEL_APPOINTMENT_CONFIRMATION}/${result.data.appointmentId}`,
        location.search,
      ),
    );
  }, [cancelAppointment, appointmentId, navigate, location.search]);

  const onAbortCancelAppointment = useCallback(() => {
    const next = new URLSearchParams(location.search.replace(/^\?/, ''));
    next.set('details', 'true');
    navigate(
      `${URLS.CONFIRMATION}/${
        appointmentData?.appointmentId
      }?${next.toString()}`,
    );
  }, [appointmentData?.appointmentId, navigate, location.search]);

  const loadingMessage = getLoadingMessage(isCanceling);

  return (
    <Wrapper
      showBackLink
      flowType={FLOW_TYPES.CANCEL}
      loading={isLoading || isCanceling}
      errorAlert={
        isCancellationFailedError(cancelAppointmentError) ||
        isMissingParameterError(cancelAppointmentError) ||
        isServerError(cancelAppointmentError) ||
        isAppointmentNotFoundError(cancelAppointmentError) ||
        isServerError(appointmentError) ||
        isAppointmentNotFoundError(appointmentError)
      }
      testID="cancel-appointment-page"
      pageTitle="Would you like to cancel this call?"
      loadingMessage={loadingMessage}
    >
      <div className="vads-u-margin-top--6">
        <AppointmentCard
          appointmentData={appointmentData}
          showAddToCalendarButton={false}
        />
      </div>
      <VaButtonPair
        data-testid="cancel-confirm-button-pair"
        leftButtonText="Yes, cancel call"
        rightButtonText="No, don’t cancel"
        onPrimaryClick={onCancelAppointment}
        onSecondaryClick={onAbortCancelAppointment}
        class="vads-u-margin-top--4"
      />
    </Wrapper>
  );
};

export default CancelAppointment;
